import { useState, useRef } from "react";
import { X, UploadCloud, Loader2, MessageCircle, AlertCircle, CheckCircle2, Plus, Trash2 } from "lucide-react";
import axios from "axios";

/**
 * Live Chat Importer — modal for uploading a Live Chat Campaign Reporting
 * PDF and persisting the extracted metrics into the isolated
 * `live_chat_reports` collection. Mirrors the ContentEffectImporter flow.
 *
 * Flow:
 *  1. Pick client + date range + drop the Live Chat PDF
 *  2. POST /api/import/live-chat/extract → returns parsed JSON
 *  3. Render editable confirmation form
 *  4. POST /api/import/live-chat/save
 */

const numInput = (value, onChange, testId, step = "1") => (
  <input
    type="number"
    min="0"
    step={step}
    value={value ?? 0}
    onChange={(e) => onChange(Number(e.target.value || 0))}
    className="w-full px-2 py-1 text-sm border border-slate-300 rounded-md focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 tabular-nums"
    data-testid={testId}
  />
);

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 block mb-0.5">{label}</label>
    {children}
  </div>
);

const blankState = () => ({
  total_chats: 0,
  total_leads: 0,
  billable_leads: 0,
  chat_to_lead_pct: 0,
  lead_types: [],
  individual_chats: [],
});

export default function LiveChatImporter({ open, onClose, clients, API, onSaved }) {
  const fileRef = useRef(null);
  const [step, setStep] = useState("upload"); // upload | extracting | confirm | saving | done
  const [clientId, setClientId] = useState("");
  const [reportStart, setReportStart] = useState("");
  const [reportEnd, setReportEnd] = useState("");
  const [locationName, setLocationName] = useState("");
  const [file, setFile] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [extracted, setExtracted] = useState(blankState());
  const [periodLabel, setPeriodLabel] = useState("");

  const selectedClient = clients.find((c) => c.id === clientId);
  const isMulti = selectedClient?.client_type === "multi";
  const locations = selectedClient?.locations || [];

  const reset = () => {
    setStep("upload");
    setClientId("");
    setReportStart("");
    setReportEnd("");
    setLocationName("");
    setFile(null);
    setErrorMsg("");
    setExtracted(blankState());
    setPeriodLabel("");
  };

  const onSelectFile = (selected) => {
    if (!selected || selected.length === 0) return;
    const f = selected[0];
    setFile(f);
  };

  const runExtract = async () => {
    if (!clientId) return setErrorMsg("Pick a client first.");
    if (!reportStart || !reportEnd) return setErrorMsg("Pick a report start and end date.");
    if (!file) return setErrorMsg("Drop the Live Chat PDF.");
    setErrorMsg("");
    setStep("extracting");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(
        `${API}/import/live-chat/extract?client_id=${clientId}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 },
      );
      const data = res.data || {};
      setExtracted({
        total_chats: data.total_chats || 0,
        total_leads: data.total_leads || 0,
        billable_leads: data.billable_leads || 0,
        chat_to_lead_pct: data.chat_to_lead_pct || 0,
        lead_types: Array.isArray(data.lead_types) ? data.lead_types : [],
        individual_chats: Array.isArray(data.individual_chats) ? data.individual_chats : [],
      });
      setPeriodLabel(data.report_period_label || "");
      setStep("confirm");
    } catch (e) {
      setErrorMsg(e.response?.data?.detail || e.message || "Extraction failed");
      setStep("upload");
    }
  };

  const runSave = async () => {
    setErrorMsg("");
    setStep("saving");
    try {
      const payload = {
        client_id: clientId,
        report_start: reportStart,
        report_end: reportEnd,
        location_name: locationName || "",
        total_chats: Number(extracted.total_chats || 0),
        total_leads: Number(extracted.total_leads || 0),
        billable_leads: Number(extracted.billable_leads || 0),
        chat_to_lead_pct: Number(extracted.chat_to_lead_pct || 0),
        lead_types: (extracted.lead_types || [])
          .filter((lt) => (lt.type || "").trim() && Number(lt.count) > 0)
          .map((lt) => ({ type: String(lt.type).trim(), count: Number(lt.count) })),
        individual_chats: extracted.individual_chats || [],
      };
      if (payload.total_chats === 0 && payload.total_leads === 0) {
        setErrorMsg("Nothing to save — both Chats and Leads are zero. Edit values or cancel.");
        setStep("confirm");
        return;
      }
      await axios.post(`${API}/import/live-chat/save`, payload, { timeout: 30000 });
      setStep("done");
      if (onSaved) onSaved();
    } catch (e) {
      setErrorMsg(e.response?.data?.detail || e.message || "Save failed");
      setStep("confirm");
    }
  };

  const updateField = (field, value) => setExtracted({ ...extracted, [field]: value });
  const updateLeadType = (i, field, value) => {
    const lts = [...(extracted.lead_types || [])];
    lts[i] = { ...lts[i], [field]: value };
    setExtracted({ ...extracted, lead_types: lts });
  };
  const removeLeadType = (i) => {
    const lts = [...(extracted.lead_types || [])];
    lts.splice(i, 1);
    setExtracted({ ...extracted, lead_types: lts });
  };
  const addLeadType = () => {
    setExtracted({
      ...extracted,
      lead_types: [...(extracted.lead_types || []), { type: "", count: 0 }],
    });
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      data-testid="live-chat-importer-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center text-white">
              <MessageCircle className="w-5 h-5" />
            </div>
            <div>
              <p className="text-lg font-bold text-slate-900">The Live Chat Story</p>
              <p className="text-xs text-slate-500">Drop your Live Chat Campaign Reporting PDF. We extract the metrics for you.</p>
            </div>
          </div>
          <button
            onClick={() => { reset(); onClose(); }}
            className="p-2 rounded-lg hover:bg-slate-100"
            data-testid="live-chat-close"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5">
          {errorMsg && (
            <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{errorMsg}</span>
            </div>
          )}

          {step === "upload" && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1">Client</label>
                  <select
                    value={clientId}
                    onChange={(e) => { setClientId(e.target.value); setLocationName(""); }}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    data-testid="lc-client-select"
                  >
                    <option value="">Select client…</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1">Report Start</label>
                  <input
                    type="date"
                    value={reportStart}
                    onChange={(e) => setReportStart(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    data-testid="lc-report-start"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1">Report End</label>
                  <input
                    type="date"
                    value={reportEnd}
                    onChange={(e) => setReportEnd(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    data-testid="lc-report-end"
                  />
                </div>
              </div>

              {isMulti && locations.length > 0 && (
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1">Location (optional, leave blank for whole-brand)</label>
                  <select
                    value={locationName}
                    onChange={(e) => setLocationName(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    data-testid="lc-location-select"
                  >
                    <option value="">Whole brand</option>
                    {locations.map((l, i) => (
                      <option key={i} value={l.name || l}>{l.name || l}</option>
                    ))}
                  </select>
                </div>
              )}

              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); onSelectFile(e.dataTransfer.files); }}
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-cyan-500 hover:bg-cyan-50/40 transition cursor-pointer"
                data-testid="lc-drop-zone"
              >
                <UploadCloud className="w-10 h-10 mx-auto text-slate-400 mb-2" />
                <p className="text-sm font-medium text-slate-700">
                  {file ? `${file.name} ready` : "Drop the Live Chat PDF here, or click to browse"}
                </p>
                <p className="text-xs text-slate-500 mt-1">One PDF at a time · e.g. Bella Terra JUNE Live Chat Campaign Reporting.pdf</p>
                <input
                  ref={fileRef}
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={(e) => onSelectFile(e.target.files)}
                  data-testid="lc-file-input"
                />
              </div>

              <div className="flex justify-end gap-2">
                <button
                  onClick={() => { reset(); onClose(); }}
                  className="px-4 py-2 text-sm rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
                >Cancel</button>
                <button
                  onClick={runExtract}
                  disabled={!clientId || !reportStart || !reportEnd || !file}
                  className="px-5 py-2 text-sm rounded-lg bg-gradient-to-br from-cyan-500 to-blue-500 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 flex items-center gap-2"
                  data-testid="lc-extract-btn"
                >
                  <MessageCircle className="w-4 h-4" /> Extract from PDF
                </button>
              </div>
            </div>
          )}

          {step === "extracting" && (
            <div className="py-16 text-center" data-testid="lc-extracting">
              <Loader2 className="w-10 h-10 mx-auto text-cyan-500 animate-spin mb-3" />
              <p className="text-sm font-medium text-slate-700">Reading your Live Chat report…</p>
              <p className="text-xs text-slate-500 mt-1">This usually takes just a few seconds.</p>
            </div>
          )}

          {step === "confirm" && (
            <div className="space-y-4">
              <div className="flex items-start gap-2 p-3 rounded-lg bg-cyan-50 border border-cyan-200 text-cyan-800 text-xs">
                <MessageCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold mb-0.5">Review extracted numbers</p>
                  <p>We extracted these from your PDF{periodLabel ? ` (period detected: ${periodLabel})` : ""}. Edit anything that doesn&apos;t match.</p>
                </div>
              </div>

              <div className="bg-white rounded-xl border-2 border-cyan-200 p-4">
                <p className="text-sm font-bold text-slate-900 mb-3">Aggregate Metrics</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                  <Field label="Total Chats">
                    {numInput(extracted.total_chats, (v) => updateField("total_chats", v), "lc-total-chats")}
                  </Field>
                  <Field label="Total Leads">
                    {numInput(extracted.total_leads, (v) => updateField("total_leads", v), "lc-total-leads")}
                  </Field>
                  <Field label="Billable Leads">
                    {numInput(extracted.billable_leads, (v) => updateField("billable_leads", v), "lc-billable-leads")}
                  </Field>
                  <Field label="Chat → Lead %">
                    {numInput(extracted.chat_to_lead_pct, (v) => updateField("chat_to_lead_pct", v), "lc-ratio", "0.01")}
                  </Field>
                </div>
              </div>

              <div className="bg-white rounded-xl border-2 border-cyan-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-bold text-slate-900">Lead Type Breakdown</p>
                  <button
                    onClick={addLeadType}
                    className="text-xs text-cyan-600 hover:text-cyan-700 font-semibold inline-flex items-center gap-1"
                    data-testid="lc-add-lead-type"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add row
                  </button>
                </div>
                {(extracted.lead_types || []).length === 0 && (
                  <p className="text-xs text-slate-400 italic">No lead types extracted. Add rows manually if needed.</p>
                )}
                <div className="space-y-2">
                  {(extracted.lead_types || []).map((lt, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        type="text"
                        placeholder="Type (e.g. Sales)"
                        value={lt.type || ""}
                        onChange={(e) => updateLeadType(i, "type", e.target.value)}
                        className="flex-1 px-2 py-1 text-sm border border-slate-300 rounded-md"
                        data-testid={`lc-lead-type-name-${i}`}
                      />
                      {numInput(lt.count, (v) => updateLeadType(i, "count", v), `lc-lead-type-count-${i}`)}
                      <button
                        onClick={() => removeLeadType(i)}
                        className="p-1.5 rounded-md hover:bg-rose-50 text-rose-500"
                        data-testid={`lc-lead-type-remove-${i}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {extracted.individual_chats?.length > 0 && (
                <div className="bg-slate-50 rounded-xl border border-slate-200 p-3 text-xs text-slate-600">
                  <span className="font-semibold">{extracted.individual_chats.length}</span> individual chat{extracted.individual_chats.length !== 1 ? "s" : ""} captured (internal-only, not shown to clients).
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() => setStep("upload")}
                  className="px-4 py-2 text-sm rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
                >Back</button>
                <button
                  onClick={runSave}
                  className="px-5 py-2 text-sm rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 flex items-center gap-2"
                  data-testid="lc-save-btn"
                >
                  <CheckCircle2 className="w-4 h-4" /> Looks Good — Save
                </button>
              </div>
            </div>
          )}

          {step === "saving" && (
            <div className="py-16 text-center" data-testid="lc-saving">
              <Loader2 className="w-10 h-10 mx-auto text-emerald-500 animate-spin mb-3" />
              <p className="text-sm font-medium text-slate-700">Saving Live Chat report…</p>
            </div>
          )}

          {step === "done" && (
            <div className="py-12 text-center" data-testid="lc-done">
              <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <p className="text-base font-bold text-slate-900 mb-1">Saved!</p>
              <p className="text-sm text-slate-600 mb-5">Open the client&apos;s report to see the new Live Chat Story section.</p>
              <div className="flex justify-center gap-2">
                <button
                  onClick={reset}
                  className="px-4 py-2 text-sm rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
                  data-testid="lc-import-another"
                >Import Another</button>
                <button
                  onClick={() => { reset(); onClose(); }}
                  className="px-5 py-2 text-sm rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700"
                  data-testid="lc-done-close"
                >Done</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
